// =============================================================================
// planReview.ts — Patch 016.0.B.
//
// Frontend client for the OpenAI plan-reviewer surface. Mirrors the
// three GET / three POST endpoints in workers/core-os/src/routes/
// plan_review.ts. All endpoints Access-gated at the perimeter; this
// client just builds the request shape.
//
// CALLER CONTRACT: this module returns the structured API response.
// Callers handle:
//   - 403 plan_review_disabled         (flag is off; show "installed but not enabled" copy)
//   - 503 openai_api_key_missing       (secret not set; same UX as flag-off)
//   - 413 plan_pdf_too_large_for_review
//   - 429 plan_review_rate_limited
//   - 404 plan_set_not_found / document_not_found / r2_object_missing
//   - 500 plan_review_unexpected_error / openai_* upstream failures
// =============================================================================

import { request } from './request'
import type {
  PlanReviewFinding,
  PlanReviewFindingAcceptResponse,
  PlanReviewFindingKind,
  PlanReviewFindingStatus,
  PlanReviewFindingTransitionResponse,
  PlanReviewRun,
  PlanReviewRunResponse,
} from './types'

export interface PlanReviewListFindingsParams {
  status?: PlanReviewFindingStatus
  kind?: PlanReviewFindingKind
}

export const planReviewApi = {
  listRuns: (projectId: string) =>
    request<{ ok: true; runs: PlanReviewRun[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/plan-review/runs`,
    ),
  listFindings: (
    projectId: string,
    params: PlanReviewListFindingsParams = {},
  ) => {
    const q = new URLSearchParams()
    if (params.status) q.append('status', params.status)
    if (params.kind) q.append('kind', params.kind)
    const qs = q.toString()
    return request<{ ok: true; findings: PlanReviewFinding[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/plan-review/findings${
        qs ? `?${qs}` : ''
      }`,
    )
  },
  run: (projectId: string, body: { plan_set_id?: string } = {}) =>
    request<PlanReviewRunResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/plan-review/run`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  accept: (
    projectId: string,
    findingId: string,
    body: { notes?: string } = {},
  ) =>
    request<PlanReviewFindingAcceptResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/plan-review/findings/${encodeURIComponent(findingId)}/accept`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  reject: (
    projectId: string,
    findingId: string,
    body: { notes?: string } = {},
  ) =>
    request<PlanReviewFindingTransitionResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/plan-review/findings/${encodeURIComponent(findingId)}/reject`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  dismiss: (
    projectId: string,
    findingId: string,
    body: { notes?: string } = {},
  ) =>
    request<PlanReviewFindingTransitionResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/plan-review/findings/${encodeURIComponent(findingId)}/dismiss`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
}
